import { Link } from 'react-router-dom';

// Utils
import cn from '@/utils/cn';

// Components
import RevealFadeIn from '@/components/reveal-animations/RevealFadeIn/RevealFadeIn';
import RevealScaleIn from '@/components/reveal-animations/RevealScaleIn/RevealScaleIn';
import Tag from '@/components/atoms/Tag/Tag';
import HeroSection from '@/components/blocks/HeroSection/HeroSection';
import Picto from '@/components/atoms/Picto/Picto';
import Paragraph from '@/components/atoms/Paragraph/Paragraph';

// Types
import { ColorThemeEnum } from '@/enum/ColorThemeEnum';
import type { CTAType } from '@/types/CTAType';
import type { HeroSectionType } from '@/components/blocks/HeroSection/HeroSectionType';
import { ClientType } from '@/views/Client/ClientType';

// Styles
import styles from './ClientHeroSection.module.css';

interface ClientHeroSectionProps {
  client: ClientType['client'];
  title: ClientType['title'];
  paragraph: ClientType['paragraph'];
  tags: ClientType['tags'];
  gradient: ClientType['gradient'];
  shape: ClientType['shape'];
}

/**
 * @deprecated ? Duplicated from blocks - src/components/blocks/HeroSection
 * @param client
 * @param title
 * @param paragraph
 * @param tags
 * @param gradient
 * @param shape
 * @constructor
 */
function ClientHeroSection({ client, title, paragraph, tags, gradient, shape }: ClientHeroSectionProps) {
  const nextClientSideLink: CTAType = {
    id: 1,
    label: client?.next?.name || '',
    link: client?.next?.slug || '',
    color: ColorThemeEnum.WHITE,
  };

  return (
    <HeroSection
      title={title}
      paragraph={paragraph}
      color={ColorThemeEnum.ORANGE}
      scale="regular"
      shape={shape as HeroSectionType['shape']}
      gradient={gradient}
      media={null}
      cta={null}
      sideCTA={nextClientSideLink}
      customContent={
        <>
          <div className={styles.wrapper}>
            <div className={styles.container}>
              <RevealFadeIn delay={0.8}>
                <title.seoTitleTag className={cn([styles.title, 'font-script'])}>{title.title}</title.seoTitleTag>
              </RevealFadeIn>

              <RevealFadeIn delay={1}>
                <h2 className={styles.text}>
                  <Paragraph text={paragraph.text} color={paragraph.color} size={paragraph.size} />
                </h2>
              </RevealFadeIn>

              {tags.length ? (
                <div className={styles.tags}>
                  {tags.map((tag, index) => (
                    <RevealFadeIn key={tag.id} delay={1.2 + (index + 1) / 10}>
                      <Tag color="white" bgColor="transparent">
                        {tag.text}
                      </Tag>
                    </RevealFadeIn>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          <RevealScaleIn offsetBeforeDisplay={0} delay={1.2} className={styles.navigation}>
            {client?.prev?.slug ? (
              <Link to={client?.prev?.slug} className={cn([styles.nav, styles.prev])}>
                <Picto icon="arrowLeft" />
              </Link>
            ) : null}
            {client?.next?.slug ? (
              <Link to={client?.next?.slug} className={cn([styles.nav, styles.next])}>
                <Picto icon="arrowRight" />
              </Link>
            ) : null}
          </RevealScaleIn>
        </>
      }
    />
  );
}

export default ClientHeroSection;
